import React, { useContext, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowRight } from "lucide-react";
import { analyticsMember } from "../../../reducers/analytics.slice";
import { getAllOrderDetail } from "../../../reducers/order.slice";
import { getAllShipmentDetail } from "../../../reducers/shipment.slice";
import { listAllPayouts } from "../../../reducers/payouts.slice";
import { getUsers, isDiscordIsVerified } from "../../../utils/getUser";
import { AuthContext } from "../../../context/authContext";
import { convertDateFormatInUtc, formatNumberToDollar } from "../../../utils/util";
import { getTierKey } from "../../../components/member/tierConfig";
import { Button } from "../../../components/ui/button";
import PageHeader from "../../../components/shared/PageHeader";
import StatCard from "../../../components/shared/StatCard";
import StatusBadge from "../../../components/shared/StatusBadge";
import SearchBar from "../../../components/shared/SearchBar";
import RefreshButton from "../../../components/shared/RefreshButton";
import DataTable from "../../../components/shared/DataTable";
import useMediaQuery from "../../../components/shared/useMediaQuery";
import { HeroBanner, QuickActionsPanel, PendingShipmentsPanel } from "./DashboardWidgets";

// Caps the sample used to derive lifetime totals client-side (no aggregate
// endpoint exists for "orders completed" / "avg payout speed").
const LIFETIME_SAMPLE_LIMIT = 200;
const PENDING_SHIPMENT_STATUSES = ["LABEL_PENDING", "PENDING_SHIPMENT"];

const STAT_ACCENTS = {
  open_orders: "#00ADB5",
  pending_quotes: "#e8b84b",
  pending_shipments: "#af58f2",
  earnings_overview: "#4caf50",
};

const STAT_ROUTES = {
  open_orders: "/dashboard/open-orders",
  pending_quotes: "/dashboard/quotes",
  pending_shipments: "/dashboard/shipment",
  earnings_overview: "/dashboard/shipment/member-payouts",
};

// Open Orders / Pending Quotes / Pending Shipments need action from the
// member; Earnings Overview is informational only — no "view" affordance.
const ACTIONABLE_STATS = new Set(["open_orders", "pending_quotes", "pending_shipments"]);

const MemberDashboard = () => {
  const [analyticsData, setAnalyticsData] = useState([]);
  const [showLoader, setShowLoader] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const user = getUsers();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 640px)");
  const { userDetails, showToast, walletSummary } = useContext(AuthContext);
  const [isDiscord, setIsDiscord] = useState(false);
  const [allLoadedData, setAllLoadedData] = useState([]);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [loadingChunk, setLoadingChunk] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const getRowsPerPage = sessionStorage.getItem("memberDashboard_rowsPerPage");
    return getRowsPerPage ? JSON.parse(getRowsPerPage) : 5;
  });
  const [totalRows, setTotalRows] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isNavigatingToPage, setIsNavigatingToPage] = useState(false);
  const [pageCache, setPageCache] = useState(new Map());
  const CHUNK_SIZE = 50;
  const isInitialRender = useRef(true);

  // Hero banner + widgets: derived from real order/payout/shipment history,
  // not returned by the dashboard analytics endpoint.
  const [heroStats, setHeroStats] = useState({ ordersCompleted: null, avgPayoutDays: null });
  const [pendingShipments, setPendingShipments] = useState({ items: [], total: 0, loading: true });
  const [earningsRaw, setEarningsRaw] = useState(0);

  useEffect(() => {
    const user = getUsers();
    const isDiscord = isDiscordIsVerified();
    const payload = {
      userId: user?.id || user?.id,
      access_token: userDetails?.access_token || user?.access_token, // Ensure `user?.id` is defined to avoid errors
    };

    dispatch(analyticsMember(payload)).then((res) => {
      if (res?.payload) {
        const dataArray = Object.keys(res.payload).map((key) => ({
          key,
          title: key
            .replace(/_/g, " ")
            .replace(/\b\w/g, (char) => char.toUpperCase()),
          value:
            key === "earnings_overview"
              ? formatNumberToDollar(res.payload[key])
              : res.payload[key],
          route:
            !isDiscord && user?.role === "MEMBER"
              ? "/user-dashboard"
              : STAT_ROUTES[key] || "/",
        }));
        setAnalyticsData(dataArray);
        setEarningsRaw(res.payload.earnings_overview || 0);
      } else {
        console.error("No payload in response:", res);
      }
    });
  }, [dispatch, user?.id]);

  useEffect(() => {
    if (!user?.id) return;

    dispatch(
      getAllOrderDetail({ skip: 0, limit: LIFETIME_SAMPLE_LIMIT, order_status: [], type: [] })
    ).then((res) => {
      const orders = res?.payload?.data || [];
      const ordersCompleted = orders.filter((o) => o.status === "APPROVED").length;
      setHeroStats((prev) => ({ ...prev, ordersCompleted }));
    });

    dispatch(
      listAllPayouts({ skip: 0, limit: LIFETIME_SAMPLE_LIMIT, payout_status: ["PAYMENT_DONE"], type: [] })
    ).then((res) => {
      const payouts = res?.payload?.data || [];
      const durations = payouts
        .filter((p) => p?.payment_date && p?.shipment_details?.shipment_date)
        .map((p) => (new Date(p.payment_date) - new Date(p.shipment_details.shipment_date)) / 86400000)
        .filter((days) => Number.isFinite(days) && days >= 0);
      const avgPayoutDays = durations.length
        ? Math.round((durations.reduce((sum, d) => sum + d, 0) / durations.length) * 10) / 10
        : null;
      setHeroStats((prev) => ({ ...prev, avgPayoutDays }));
    });

    dispatch(
      getAllShipmentDetail({
        skip: 0,
        limit: 3,
        shipment_status: PENDING_SHIPMENT_STATUSES,
        shipment_type: [],
      })
    ).then((res) => {
      setPendingShipments({
        items: res?.payload?.data || [],
        total: res?.payload?.total ?? 0,
        loading: false,
      });
    });
  }, [dispatch, user?.id]);

  const loadDataChunk = async (chunkNumber = 0) => {
    // Create consistent filter key (sort arrays for stable keys)
    const filterKey = JSON.stringify({
      search: searchTerm,
    });
    const cacheKey = `chunk-${chunkNumber}-${filterKey}`;

    // Check cache first
    if (pageCache.has(cacheKey)) {
      const cachedData = pageCache.get(cacheKey);
      if (cachedData.totalRows !== undefined) {
        setTotalRows(cachedData.totalRows);
      }
      return cachedData.data;
    }

    // Prevent multiple simultaneous calls
    if (loadingChunk) {
      return [];
    }

    setLoadingChunk(true);
    if (chunkNumber === 0 && allLoadedData.length === 0) {
      setShowLoader(true);
    }

    try {
      const payload = {
        skip: chunkNumber * CHUNK_SIZE,
        limit: CHUNK_SIZE,
      };

      if (searchTerm && searchTerm?.trim() !== "") {
        payload.search = searchTerm?.trim();
      }

      const res = await dispatch(getAllOrderDetail(payload));
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setHasMoreData(false);
        setFilteredData([]);
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      if (
        !res?.payload ||
        !res?.payload?.data ||
        !Array.isArray(res.payload.data)
      ) {
        console.log("No valid data in response");
        setHasMoreData(false);
        setFilteredData([]);
        return;
      }

      if ((chunkNumber === 0 || !totalRows) && res?.payload?.total) {
        setTotalRows(res?.payload?.total);
      }

      const formattedOrder = res?.payload?.data?.map((item) => {
        const createdDate = convertDateFormatInUtc(
          item?.created_at,
          "MMM D, YYYY · h:mm A"
        );
        const orderPrice = formatNumberToDollar(item?.order_price);
        const newOrderItems = item?.order_items?.map((orderItem) => {
          const buy_price = formatNumberToDollar(orderItem?.buy_price || 0);
          const cashout_price = formatNumberToDollar(
            orderItem?.cashout_price || 0
          );
          const total_amount = formatNumberToDollar(
            orderItem?.total_amount || 0
          );
          return {
            ...orderItem,
            buy_price,
            cashout_price,
            total_amount,
          };
        });

        return {
          ...item,
          newOrderItems,
          createdDate: createdDate,
          orderPrice,
        };
      });
      // Cache the chunk
      setPageCache(
        (prev) =>
          new Map(
            prev.set(cacheKey, {
              data: formattedOrder,
              timestamp: Date.now(),
              totalRows: res?.payload?.total,
            })
          )
      );

      const hasMore = formattedOrder?.length === CHUNK_SIZE;
      setHasMoreData(hasMore);
      return formattedOrder;
    } catch (error) {
      console.error("Error in loadDataChunk:", error);
      showToast("Error fetching data", "error");
      setHasMoreData(false);
      return [];
    } finally {
      setLoadingChunk(false);
      setShowLoader(false);
    }
  };

  const columns = [
    {
      key: "id",
      label: "Order ID",
      width: "16%",
      render: (row) => <span style={{ fontWeight: 700, color: "#fff" }}>{row.id}</span>,
    },
    {
      key: "orderPrice",
      label: "Order Price",
      width: "12%",
      render: (row) => <span style={{ fontWeight: 700, color: "#fff" }}>{row.orderPrice}</span>,
    },
    { key: "type", label: "Order Type", width: "14%" },
    { key: "created_by_name", label: "Member Name", width: "18%" },
    { key: "createdDate", label: "Order Date", width: "20%" },
    {
      key: "status",
      label: "Status",
      width: "20%",
      render: (row) => <StatusBadge status={row.status} />,
    },
  ];

  const itemsColumns = [
    { key: "id", label: "Item ID" },
    { key: "item_name", label: "Item Name" },
    { key: "upc", label: "UPC" },
    { key: "quote_id", label: "Quote ID" },
    { key: "quantity", label: "Quantity" },
    { key: "buy_price", label: "Buy Price" },
    { key: "cashout_price", label: "Cashout Price" },
    { key: "retailer_name", label: "Retailer Name" },
    { key: "total_amount", label: "Total Amount" },
    { key: "notes", label: "Notes" },
    {
      key: "status",
      label: "Status",
      render: (row) => <StatusBadge status={row.status} />,
    },
  ];

  const loadInitialData = async () => {
    const initialChunk = await loadDataChunk(0);
    setAllLoadedData(initialChunk);
  };

  const checkAndLoadMoreData = async (page, rowsPerPage) => {
    const requiredDataLength = (page + 1) * rowsPerPage;
    const currentDataLength = allLoadedData.length;

    if (currentDataLength >= requiredDataLength) {
      console.log("Have enough data already");
      return;
    }

    if (!hasMoreData) {
      console.log("No more data available from server");
      return;
    }

    if (loadingChunk) {
      console.log("Already loading");
      return;
    }

    // CRITICAL: Check against total rows if available
    if (totalRows > 0 && currentDataLength >= totalRows) {
      setHasMoreData(false);
      return;
    }
    try {
      setIsNavigatingToPage(true);
      const nextChunk = Math.floor(currentDataLength / CHUNK_SIZE);
      const targetPage = page;
      const newData = await loadDataChunk(nextChunk);
      if (newData && newData?.length > 0) {
        setAllLoadedData((prev) => {
          const updated = [...prev, ...newData];
          return updated;
        });

        setTimeout(() => {
          const startIndex = page * rowsPerPage;
          const endIndex = startIndex + rowsPerPage;
          const combinedData = [...allLoadedData, ...newData];
          const pageData = combinedData.slice(startIndex, endIndex);
          setCurrentPage(targetPage);
          setFilteredData(pageData);
        }, 100);
      } else {
        console.log("No new data received, setting hasMoreData to false");
        setHasMoreData(false);
      }
    } catch (error) {
      console.error("Error loading more data:", error);
      showToast("Error loading data", "error");
    } finally {
      setIsNavigatingToPage(false);
    }
  };

  // FIXED: Handle page change with forced data update
  const handleChangePage = async (event, newPage) => {
    if (isNavigatingToPage || loadingChunk) return;

    const requiredDataLength = (newPage + 1) * rowsPerPage;

    try {
      setIsNavigatingToPage(true);

      if (allLoadedData.length < requiredDataLength && hasMoreData) {
        await checkAndLoadMoreData(newPage, rowsPerPage);
      } else {
        const startIndex = newPage * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        const pageData = allLoadedData.slice(startIndex, endIndex);

        setCurrentPage(newPage);
        setFilteredData(pageData);
      }
    } catch (error) {
      console.error("Error changing page:", error);
      showToast("Error loading page data", "error");
    } finally {
      setIsNavigatingToPage(false);
    }
  };

  useEffect(() => {
    if (isNavigatingToPage || loadingChunk) {
      return;
    }

    if (!allLoadedData?.length) return;

    const startIndex = currentPage * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;

    const pageData = allLoadedData.slice(startIndex, endIndex);
    if (pageData.length > 0 && !isNavigatingToPage && !loadingChunk) {
      setFilteredData(pageData);
    } else {
      console.warn("No data available for current page:", currentPage);
      if (currentPage > 0) {
        console.log("Attempting to go back to previous page");
        setCurrentPage((prev) => prev - 1);
      }
    }
  }, [rowsPerPage, allLoadedData]);

  // Handle rows per page change
  const handleChangeRowsPerPage = async (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    sessionStorage.setItem("memberDashboard_rowsPerPage", JSON.stringify(newRowsPerPage));
    setCurrentPage(0);

    const requiredDataLength = newRowsPerPage; // For page 0
    if (
      allLoadedData.length < requiredDataLength &&
      hasMoreData &&
      !loadingChunk
    ) {
      checkAndLoadMoreData(0, newRowsPerPage);
    }
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }

    if (isSearching) return;

    const performSearch = async () => {
      try {
        setIsSearching(true);
        setCurrentPage(0);
        setAllLoadedData([]);

        const results = await loadDataChunk(0);
        if (results?.length > 0) {
          setAllLoadedData(results);
        } else {
          setFilteredData([]);
          setAllLoadedData([]);
        }
      } finally {
        setIsSearching(false);
      }
    };

    performSearch();
  }, [debouncedSearch]);

  const handleRefresh = () => {
    const currentPageBeforeRefresh = currentPage;

    setPageCache(new Map()); // Clear cache
    setAllLoadedData([]); // Clear loaded data
    setHasMoreData(true); // Reset has more data flag

    loadInitialData().then(() => {
      if (currentPageBeforeRefresh > 0) {
        setTimeout(() => {
          setCurrentPage(currentPageBeforeRefresh);
        }, 100);
      }
    });
  };

  useEffect(() => {
    const isDiscord = isDiscordIsVerified();
    setIsDiscord(isDiscord);
  }, []);

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {!isDiscord && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            padding: "12px 16px",
            marginBottom: 34,
            borderRadius: 12,
            background: "rgba(255,193,7,0.08)",
            border: "1px solid rgba(255,193,7,0.18)",
            color: "#ffc107",
            fontSize: 13,
            flexWrap: "wrap",
          }}
        >
          <AlertTriangle size={16} style={{ flexShrink: 0, marginRight: 10 }} />
          <span style={{ flex: 1, marginRight: 10 }}>
            Your Discord username is not linked. Access to actions is restricted until you
            complete the verification process.
          </span>
          <span
            style={{ textDecoration: "underline", cursor: "pointer", fontWeight: 700 }}
            onClick={() => navigate("/link-discord")}
          >
            Click here!
          </span>
        </div>
      )}

      <div style={{ marginBottom: isMobile ? 24 : 34 }}>
        <PageHeader title={`Welcome back, ${user?.first_name || ""}`} />
      </div>

      <div style={{ marginBottom: isMobile ? 24 : 34 }}>
        <HeroBanner
          earningsLabel={formatNumberToDollar(earningsRaw)}
          earningsRaw={earningsRaw}
          ordersCompleted={heroStats.ordersCompleted}
          avgPayoutDays={heroStats.avgPayoutDays}
          readyToWithdraw={
            walletSummary?.current_balance != null
              ? formatNumberToDollar(walletSummary.current_balance)
              : null
          }
          tierKey={getTierKey(user)}
        />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "repeat(auto-fit, minmax(145px, 1fr))" : "repeat(auto-fit, minmax(200px, 1fr))",
          gap: isMobile ? 12 : 16,
          marginBottom: isMobile ? 24 : 34,
        }}
      >
        {analyticsData.map((data) => (
          <StatCard
            key={data.key}
            label={data.title}
            value={data.value}
            accent={STAT_ACCENTS[data.key] || "#00ADB5"}
            actionLabel={ACTIONABLE_STATS.has(data.key) ? "view →" : undefined}
            onClick={() => navigate(data.route)}
          />
        ))}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "stretch", marginBottom: isMobile ? 24 : 34 }}>
        <QuickActionsPanel
          walletBalanceLabel={formatNumberToDollar(walletSummary?.current_balance)}
          disabled={!isDiscord}
          style={{ marginRight: isMobile ? 0 : 18, marginBottom: 18, flexBasis: isMobile ? "100%" : 260 }}
        />
        <PendingShipmentsPanel
          shipments={pendingShipments.items}
          total={pendingShipments.total}
          loading={pendingShipments.loading}
          style={{ marginBottom: 18, flexBasis: isMobile ? "100%" : 320 }}
        />
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: isMobile ? "stretch" : "center",
          marginBottom: 16,
          flexWrap: "wrap",
          gap: isMobile ? 12 : 0,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <h2 style={{ fontSize: 16, fontWeight: 800, color: "#fff", margin: 0 }}>
            Orders Overview
          </h2>
          <p style={{ fontSize: 12.5, color: "#c3c8cc", margin: "3px 0 0" }}>
            Manage and track your current open orders.
          </p>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: isMobile ? "stretch" : "center",
            flexWrap: "wrap",
            flexShrink: isMobile ? 1 : 0,
            width: isMobile ? "100%" : "auto",
            gap: isMobile ? 10 : 0,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flexShrink: isMobile ? 1 : 0,
              width: isMobile ? "100%" : "auto",
              gap: isMobile ? 8 : 12,
            }}
          >
            {!isMobile && <RefreshButton onRefresh={handleRefresh} />}
            <div style={{ flex: isMobile ? 1 : "none", minWidth: 0 }}>
              <SearchBar
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                setDebouncedTerm={setDebouncedSearch}
              />
            </div>
            {isMobile && (
              <Button
                variant="link"
                onClick={() => isDiscord && navigate("/dashboard/open-orders")}
                disabled={!isDiscord}
                className="tw-p-0 tw-h-auto"
                style={{
                  flexShrink: 0,
                  textDecoration: "none",
                  minHeight: 34,
                  padding: "0 2px",
                  justifyContent: "center",
                  whiteSpace: "nowrap",
                }}
              >
                View all
                <ArrowRight size={13} style={{ marginLeft: 6 }} />
              </Button>
            )}
          </div>

          {!isMobile && (
            <Button
              variant="link"
              onClick={() => isDiscord && navigate("/dashboard/open-orders")}
              disabled={!isDiscord}
              className="tw-p-0 tw-h-auto"
              style={{ flexShrink: 0, textDecoration: "none", marginLeft: 32 }}
            >
              View all
              <ArrowRight size={13} style={{ marginLeft: 6 }} />
            </Button>
          )}
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={filteredData}
        nestedColumns={itemsColumns}
        nestedRowKey="newOrderItems"
        nestedTableHeader="Order Items"
        totalRows={totalRows}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onPageChange={(newPage) => handleChangePage(null, newPage)}
        onRowsPerPageChange={(newValue) =>
          handleChangeRowsPerPage({ target: { value: newValue } })
        }
        loading={loadingChunk || isNavigatingToPage}
      />
    </div>
  );
};

export default MemberDashboard;
